import { useAtomValue } from 'jotai'

import { keepPreviousData, skipToken, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { getCurrencyAddress, getWrappedToken } from '@cowprotocol/common-utils'
import { getAddressKey } from '@cowprotocol/cow-sdk'
import type { Currency } from '@cowprotocol/currency'

import { PRICE_CHART_REFRESH_INTERVAL } from '../config/priceChart.constants'
import { toMarketCapBars } from '../lib/loadPriceChartHistory'
import { getTimeRangeConfig } from '../lib/priceChart.utils'
import { priceHistoryQueryOptions } from '../lib/priceHistoryQuery.utils'
import { priceChartAutoRefreshAtom } from '../state/priceChartAutoRefreshAtom'

import type { Candle, ChartMetric, SupplyVariant, TimeRange } from '../lib/priceChart.types'

export function usePriceChartHistory(
  currency: Currency | undefined,
  period: TimeRange,
  metric: ChartMetric,
  supplyVariant: SupplyVariant,
): UseQueryResult<Candle[]> {
  const autoRefresh = useAtomValue(priceChartAutoRefreshAtom)
  const queryClient = useQueryClient()
  const token = currency ? getWrappedToken(currency) : undefined
  const chainId = token?.chainId
  const address = token ? getAddressKey(token.address) : undefined
  return useQuery({
    queryKey: [
      'priceChart',
      'history',
      chainId,
      metric === 'marketCap' && currency ? getAddressKey(getCurrencyAddress(currency)) : address,
      period,
      metric,
      metric === 'marketCap' ? supplyVariant : null,
    ],
    queryFn: currency
      ? async () => {
          const { from, interval, to } = getTimeRangeConfig(period, Date.now() / 1000)
          const bars = await queryClient.fetchQuery(priceHistoryQueryOptions(currency, from, to, interval))

          return metric === 'price' ? bars : toMarketCapBars(currency, bars, supplyVariant)
        }
      : skipToken,
    placeholderData: keepPreviousData,
    refetchInterval: autoRefresh ? PRICE_CHART_REFRESH_INTERVAL : false,
    refetchOnWindowFocus: autoRefresh,
    refetchOnReconnect: autoRefresh,
    retry: false,
  })
}
